import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChoiceCard from "./ChoiceCard";
import DocumentReviewDialog from "./DocumentReviewDialog";
import { BusyOverlay, StepActions, StepHeading, primaryButtonSx } from "./EnrollmentShell";
import {
  enrollmentApi,
  newRequestKey,
  type ConsentDocument,
  type DocumentUpdates,
  type DocumentUpdatesReview,
  type EnrollmentError,
  type EnrollmentState,
  type ReconsentResult,
} from "./enrollmentApi";

interface DocumentUpdatesStepProps {
  /** "updates" (CONS-4, the default): revised documents. "reconsent" (CONS-3): the clinical consent, given again. */
  mode?: "updates" | "reconsent";
  updates?: DocumentUpdates;
  onAccepted: (result: EnrollmentState | ReconsentResult) => void;
}

/**
 * CONS-4: a member signs the new version of documents that changed materially since they joined.
 * The same evidence as enrollment — each document is a frozen, hashed snapshot the patient can read
 * in full — but one acceptance covers them all, since there are no separate choices to make.
 * Before the due date this is reached from a reminder; after it, the app shows nothing else.
 */
export default function DocumentUpdatesStep({ mode = "updates", updates, onAccepted }: DocumentUpdatesStepProps) {
  const reconsent = mode === "reconsent";
  const [review, setReview] = useState<DocumentUpdatesReview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState<ConsentDocument | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per snapshot: a retried click is the same acceptance, never a second one.
  const requestKey = useRef(newRequestKey());

  const load = async () => {
    setLoadError(null);
    try {
      setReview(await (reconsent ? enrollmentApi.reviewReconsent() : enrollmentApi.reviewUpdates()));
      requestKey.current = newRequestKey();
    } catch (e) {
      setLoadError((e as EnrollmentError).message);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const missing = [
    !accepted && (reconsent ? "accept the consent" : "accept the updated documents"),
    !nameConfirmed && "confirm your legal name",
  ].filter(Boolean) as string[];

  const submit = async () => {
    if (!review) return;
    if (missing.length > 0) {
      setShowMissing(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ids = review.documents.map((d) => d.artifactId);
      onAccepted(
        await (reconsent ? enrollmentApi.reconsent(requestKey.current, ids) : enrollmentApi.acceptUpdates(requestKey.current, ids)),
      );
    } catch (e) {
      const err = e as EnrollmentError;
      if (err.code === "REVIEW_STALE") {
        // Something changed underneath (a newer version, a name change): start over on fresh copies.
        setError(err.message);
        setAccepted(false);
        setNameConfirmed(false);
        setReview(null);
        await load();
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <StepHeading
        title={reconsent ? "Give your consent to treatment again" : "We've updated some of your documents"}
        subtitle={
          reconsent
            ? "Read the current consent and sign it to resume care. Your earlier withdrawal stays in your records."
            : updates?.blocking
              ? "Please read and sign the new versions to keep using your account."
              : `Please read and sign the new versions by ${updates?.dueByDisplay}. You can keep using your account as usual until then.`
        }
      />

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Try again
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {!review && !loadError && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress aria-label="Loading" />
        </Box>
      )}

      {review && (
        <>
          <Stack spacing={2}>
            {review.documents.map((doc) => {
              const update = updates?.documents.find((u) => u.documentId === doc.documentId);
              return (
                <Box
                  key={doc.artifactId}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", p: 2 }}
                >
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
                    <Box
                      sx={(theme) => ({
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.dark",
                      })}
                    >
                      <DescriptionOutlinedIcon aria-hidden />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                        <Typography component="h3" sx={{ fontWeight: 700 }}>
                          {doc.title}
                        </Typography>
                        {doc.isTestContent && <Chip size="small" color="warning" variant="outlined" label="TEST" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {update ? `You signed version ${update.acceptedVersion}. This is version ${doc.version}.` : `Version ${doc.version}`}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setOpen(doc)}
                        aria-label={`Read ${doc.title}`}
                        sx={{ px: 0, mt: 0.5, textTransform: "none", fontWeight: 600 }}
                      >
                        Read the full document
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Your signature
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {review.intentText}
            </Typography>
            <Box sx={{ mb: 2, px: 2, py: 1.5, borderRadius: 2, border: 1, borderStyle: "dashed", borderColor: "text.disabled" }}>
              <Typography variant="caption" color="text.secondary">
                Signing as
              </Typography>
              <Typography sx={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: "1.75rem", lineHeight: 1.2 }}>
                {review.signingName}
              </Typography>
            </Box>
            <Stack spacing={1}>
              <ChoiceCard
                kind="checkbox"
                selected={accepted}
                onClick={() => setAccepted((v) => !v)}
                label={
                  reconsent
                    ? `I have read and accept the ${review.documents[0]?.title}.`
                    : review.documents.length === 1
                      ? `I have read and accept the updated ${review.documents[0].title}.`
                      : "I have read and accept the updated documents listed above."
                }
              />
              <ChoiceCard
                kind="checkbox"
                selected={nameConfirmed}
                onClick={() => setNameConfirmed((v) => !v)}
                label="This is my full legal name. (If it is wrong, contact us before signing.)"
              />
            </Stack>
          </Box>

          {showMissing && missing.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }} role="alert">
              Before signing, please {missing.join("; ")}.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} role="alert">
              {error}
            </Alert>
          )}

          <StepActions>
            <Button variant="contained" size="large" fullWidth sx={primaryButtonSx} onClick={submit} disabled={submitting}>
              {submitting ? "Signing…" : reconsent ? "Sign and resume care" : "Sign the updated documents"}
            </Button>
          </StepActions>
        </>
      )}

      <DocumentReviewDialog document={open} onClose={() => setOpen(null)} />
      <BusyOverlay open={submitting} message="Signing your documents…" />
    </Box>
  );
}
